/**
 * §A · El marco del back office. **Marca neutra siempre.**
 *
 * `BFF_BO.md` §10: la marca del club aquí está prohibida, porque **se pierde la
 * referencia de en qué club estás**. Un back office teñido de verde porque el club
 * que miras es verde es un back office donde alguien acaba escribiendo en la cuenta
 * equivocada.
 *
 * Esto era `backoffice/App.tsx`; ahora es solo el marco y las pantallas son rutas.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useSesion, useYo } from '@/shared/auth/sesionContexto';
import { SECCIONES_ADMIN, type SeccionAdmin } from '@/backoffice/secciones';
import {
  aPendientesDeKiap,
  usePendientesDeKiap,
} from '@/backoffice/pendientes';
import { CambiarAspecto } from '@/shared/ui/CambiarAspecto';
import { BotonDelManual } from '@/shared/ui/BotonDelManual';
import { BarraInferior } from '@/shared/ui/BarraInferior';
import { Icono, type NombreDeIcono } from '@/shared/ui/Icono';
import { MenuDeCuenta } from '@/shared/ui/MenuDeCuenta';
import { Campana } from '@/shared/ui/pendientes/Campana';
import { FranjaDeUrgentes } from '@/shared/ui/pendientes/FranjaDeUrgentes';
import { usePestanaActivaALaVista } from '@/shared/ui/usePestanaActivaALaVista';
import { useTextosPlegables } from '@/shared/ui/useTextosPlegables';

/** Las variables de marca del panel, que aquí se quitan una a una. */
const VARIABLES_DE_MARCA = [
  '--marca',
  '--marca-texto',
  '--marca-interfaz',
  '--marca-contraste',
  '--marca-suave',
  '--marca-borde',
  '--marca-hundido',
];

/**
 * El club sobre el que se va a abrir modo soporte.
 *
 * **No va en la URL** y es deliberado: es el paso intermedio de una acción, no un
 * sitio al que volver, y un enlace que abriera el formulario de «entrar en el club
 * de otro» invita a pulsarlo sin leerlo.
 */
interface ContextoDelBackOffice {
  readonly clubParaSoporte: string | undefined;
  readonly pedirSoporteDe: (clubId: string) => void;
  readonly olvidarSoporte: () => void;
}

const Contexto = createContext<ContextoDelBackOffice | null>(null);

const ICONO_DE_SECCION: Record<SeccionAdmin, NombreDeIcono> = {
  clubes: 'clubes',
  comercial: 'comercial',
  dinero: 'euro',
  soporte: 'soporte',
  operacion: 'operacion',
  catalogos: 'catalogos',
  directorio: 'directorio',
};

/**
 * Las tres de la barra de abajo del móvil, tras «Hoy». Lo que se abre con el
 * teléfono en la mano: una cuenta, un lead, alguien que llama. Lo demás, en «Más».
 */
const EN_LA_BARRA: readonly SeccionAdmin[] = ['clubes', 'comercial', 'soporte'];

export function useContextoDelBackOffice(): ContextoDelBackOffice {
  const valor = useContext(Contexto);
  if (!valor) {
    throw new Error(
      'Esta pantalla está fuera del marco del back office: cuélgala de la ruta «admin».'
    );
  }
  return valor;
}

export function MarcoDelBackOffice() {
  const yo = useYo();
  const { salir } = useSesion();
  usePestanaActivaALaVista();
  const lienzo = useRef<HTMLElement>(null);
  useTextosPlegables(lienzo);
  const camino = useRouterState({ select: (s) => s.location.pathname });
  const navegar = useNavigate();
  const [clubParaSoporte, setClubParaSoporte] = useState<string | undefined>(
    undefined
  );

  // Lo pendiente (§A.8): la campana, la franja de lo urgente y la pantalla Hoy.
  const pendientes = usePendientesDeKiap();
  const lista = pendientes.data
    ? aPendientesDeKiap(pendientes.data.pending)
    : undefined;

  // Cambiar de sección olvida el club que se iba a mirar en soporte.
  const ir = (destino: string) => {
    setClubParaSoporte(undefined);
    void navegar({ to: destino });
  };

  /*
   * Ninguna variable de marca. Si el panel las dejó puestas —se llega aquí sin
   * recargar— se quitan: sin esto, el back office heredaría el verde del último
   * club visitado, que es exactamente lo que §10 prohíbe.
   */
  useEffect(() => {
    document.title = 'Back office · Kiap';
    const raiz = document.documentElement;
    for (const nombre of VARIABLES_DE_MARCA) raiz.style.removeProperty(nombre);
  }, []);

  const valor = useMemo<ContextoDelBackOffice>(
    () => ({
      clubParaSoporte,
      pedirSoporteDe: setClubParaSoporte,
      olvidarSoporte: () => setClubParaSoporte(undefined),
    }),
    [clubParaSoporte]
  );

  return (
    <Contexto.Provider value={valor}>
      <div className="backoffice">
        <header className="cabecera cabecera-neutra">
          <div className="cabecera-izq">
            <strong>Kiap</strong>
            <span className="chapa">Back office</span>
          </div>
          <div className="cabecera-der">
            <Campana
              pendientes={lista}
              cargando={pendientes.isPending}
              alIr={ir}
            />
            {/* En el ordenador, a la vista; en el móvil, en el menú de cuenta. */}
            <div className="cabecera-escritorio">
              <CambiarAspecto />
              <BotonDelManual de="backoffice" />
              {/*
                El correo lleva a la cuenta. Era texto muerto, y es donde todo el
                mundo busca «cambiar mi contraseña». 22-09-2026.
              */}
              <Link to="/mi-cuenta" className="quien" title={yo.user.email}>
                {yo.user.email}
              </Link>
              <button
                type="button"
                className="btn-plano"
                onClick={() => void salir()}
              >
                Salir
              </button>
            </div>
            <div className="cabecera-movil">
              <MenuDeCuenta
                nombre={yo.user.first_name}
                correo={yo.user.email}
                manual="backoffice"
                alSalir={() => void salir()}
              />
            </div>
          </div>
        </header>

        {/* En Hoy, no: la lista está justo debajo. */}
        {!camino.startsWith('/admin/hoy') && (
          <FranjaDeUrgentes pendientes={lista} alIr={ir} todas="/admin/hoy" />
        )}

        <nav className="secciones" aria-label="Secciones del back office">
          <Link
            to="/admin/hoy"
            className={
              camino.startsWith('/admin/hoy')
                ? 'seccion-activa-neutra'
                : 'btn-plano'
            }
            onClick={() => setClubParaSoporte(undefined)}
          >
            <Icono nombre="inicio" />
            Hoy
          </Link>
          {SECCIONES_ADMIN.map((s) => {
            const destino = `/admin/${s.id}`;
            return (
              <Link
                key={s.id}
                to={destino}
                className={
                  camino.startsWith(destino)
                    ? 'seccion-activa-neutra'
                    : 'btn-plano'
                }
                // Cambiar de sección olvida el club que se iba a mirar en soporte.
                onClick={() => setClubParaSoporte(undefined)}
              >
                <Icono nombre={ICONO_DE_SECCION[s.id]} />
                {s.etiqueta}
              </Link>
            );
          })}
        </nav>

        <BarraInferior
          etiqueta="Secciones del back office"
          alIr={ir}
          entradas={[
            {
              id: 'hoy',
              etiqueta: 'Hoy',
              icono: 'inicio',
              destino: '/admin/hoy',
              activa: camino.startsWith('/admin/hoy'),
              aviso: lista?.length || undefined,
            },
            ...SECCIONES_ADMIN.filter((s) => EN_LA_BARRA.includes(s.id)).map(
              (s) => ({
                id: s.id,
                etiqueta: s.etiqueta,
                icono: ICONO_DE_SECCION[s.id],
                destino: `/admin/${s.id}`,
                activa: camino.startsWith(`/admin/${s.id}`),
              })
            ),
          ]}
          mas={SECCIONES_ADMIN.filter((s) => !EN_LA_BARRA.includes(s.id)).map(
            (s) => ({
              id: s.id,
              etiqueta: s.etiqueta,
              icono: ICONO_DE_SECCION[s.id],
              destino: `/admin/${s.id}`,
              activa: camino.startsWith(`/admin/${s.id}`),
            })
          )}
        />

        <main className="lienzo" ref={lienzo}>
          <Outlet />
        </main>
      </div>
    </Contexto.Provider>
  );
}
